import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { forwardRef, useImperativeHandle, useMemo } from 'react';

import Card from '~/shared/components/Card';
import { useXFetch } from '~/shared/utils/useXFetch';
import { SelectOption } from '~/shared/utils/types';
import { API_FILTER_KEY } from '~/shared/utils/constants';

import { getApiFilterFromSelectOption } from '../FleetManagerPage/helpers';
import UtlizationChart from '../common/UtlizationChart';
import { dateFilters } from '../common/helpers';

import SavingsSection from './SavingsSection';

type SavingStatsResponse = {
  yearlyEstimatedSavings: number;
  yearlyPotentialSavings: number;
  capacityCoverage: number;
  overallCapacity: number;
};

function EmptyState() {
  return (
    <div className="w-full h-full flex justify-center items-center text-[12px]">No Results</div>
  );
}

function SavingCharts({ queryUrl }: { queryUrl: string }) {
  const api = useXFetch();
  const { data, isLoading, isRefetching, error } = useQuery({
    queryKey: [queryUrl],
    queryFn: () => api<SavingStatsResponse>(queryUrl),
    refetchOnWindowFocus: false,
  });

  const { yearlyEstimatedSavings, yearlyPotentialSavings, capacityCoverage, overallCapacity } =
    data || {};

  if (error || (!data && !isLoading && !isRefetching)) {
    return <EmptyState />;
  }

  return (
    <div className="flex flex-col gap-[51px] justify-center">
      <div className="text-[18px] h-[48px] flex items-center leading-[24px] font-medium w-fit">
        Savings Potential
      </div>

      <div className="flex h-full gap-8 justify-between px-2">
        <SavingsSection
          variant="purple"
          savingsText={`$${yearlyEstimatedSavings}`}
          potentialText={`$${yearlyPotentialSavings} full potential`}
          percentage={
            yearlyPotentialSavings && yearlyEstimatedSavings
              ? Math.round((yearlyEstimatedSavings / yearlyPotentialSavings) * 100)
              : 0
          }
          title="Yearly Saving using Datafy"
          potentialLegendText="Full Potential"
          savingLegendText="Estimated"
          loading={isLoading || isRefetching}
        />
        <SavingsSection
          variant="green"
          savingsText={`${capacityCoverage}GiB`}
          potentialText={`${overallCapacity}GiB`}
          percentage={
            capacityCoverage && overallCapacity
              ? Math.round((capacityCoverage / overallCapacity) * 100)
              : 0
          }
          title="EBS Covered by Datafy"
          potentialLegendText="Overall Capacity"
          savingLegendText="Datafy Coverage"
          loading={isLoading || isRefetching}
        />
      </div>
    </div>
  );
}

export default forwardRef(function DashboardCharts(_props, ref) {
  const [searchParams, setSearchParams] = useSearchParams();
  const apiFilters = searchParams.get(API_FILTER_KEY);
  const utilizationDateFilterKey = 'utilization_date_filter';
  const dateFilter = searchParams.get(utilizationDateFilterKey);

  const activeDateFilter = useMemo(
    () =>
      dateFilters.find((f) => f.title === dateFilter) ??
      dateFilters.find((f) => f.title === 'Last 3 hours')!,
    [dateFilter]
  );

  const parsedApiFilters: string[] = JSON.parse(apiFilters || '[]')?.map((f: SelectOption) =>
    getApiFilterFromSelectOption(f)
  );

  const qs = new URLSearchParams([
    ...parsedApiFilters.map((f) => ['filters', f]),
    ['start_date', activeDateFilter?.filter.start_date],
    ['end_date', activeDateFilter?.filter.end_date],
  ]);

  const savingsQs = new URLSearchParams([...parsedApiFilters.map((f) => ['filters', f])]);

  const savingsQueryUrl = `/api/v1/volumes/savings-stats?${savingsQs.toString()}`;
  const utilizationQueryUrl = `/api/v1/volumes/utilization-graph?${qs.toString()}`;

  useImperativeHandle(
    ref,
    () => ({
      savingsQueryKey: savingsQueryUrl,
      utilizationQueryKey: utilizationQueryUrl,
    }),
    [savingsQueryUrl, utilizationQueryUrl]
  );

  return (
    <div className="w-full grid grid-cols-2 gap-[24px] mb-[24px] h-[444px]">
      <Card className="flex flex-col">
        <SavingCharts queryUrl={savingsQueryUrl} />
      </Card>

      <UtlizationChart
        activeFilter={activeDateFilter}
        onSelect={(filter) => {
          setSearchParams((params) => {
            params.set(utilizationDateFilterKey, filter.title);
            return params;
          });
        }}
        queryString={qs.toString()}
      />
    </div>
  );
});
